import { useMutation, useQuery, useQueryClient } from "react-query";
import rpApi, { useOrgId, useUserInfo } from "@src/common/reliablePartnerApi";

export enum ContactType {
  default = "default",
  custom = "custom",
}

export interface ContactInfo {
  email: string | null;
  phone: string | null;
}

interface UpdateInfo {
  userId: string;
  updatedAt: Date;
}

export interface Contacts {
  custom: ContactInfo | null;
  default: ContactInfo;
  emailType: ContactType;
  phoneType: ContactType;
  updateInfo: UpdateInfo | null;
}

export const useFetchContacts = () => {
  const selectedOrgId = useOrgId();
  return useQuery(
    ["contactsFetch", selectedOrgId],
    async () => {
      const response = await rpApi.get<Contacts>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/api/orgs/${selectedOrgId}/contacts`
      );
      return response.data;
    },
    {
      enabled: !!selectedOrgId,
    }
  );
};

export const useSaveContacts = () => {
  const selectedOrgId = useOrgId();
  const userInfo = useUserInfo();
  const queryClient = useQueryClient();
  return useMutation<Contacts, unknown, ContactInfo>(
    ["contactsSave", selectedOrgId],
    async (customContacts: ContactInfo) => {
      void queryClient.cancelQueries(["contactsFetch", selectedOrgId]);
      queryClient.setQueryData<Contacts>(
        ["contactsFetch", selectedOrgId],
        (contacts: Contacts | undefined) => ({
          default: { email: null, phone: null },
          ...(contacts || {}),
          emailType: customContacts.email
            ? ContactType.custom
            : ContactType.default,
          phoneType: customContacts.phone
            ? ContactType.custom
            : ContactType.default,
          custom: customContacts,
          updateInfo: {
            userId: userInfo.data?.qvarnId || "",
            updatedAt: new Date(),
          },
        })
      );
      const response = await rpApi.post<Contacts>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/api/orgs/${selectedOrgId}/contacts`,
        customContacts
      );
      return response.data;
    },
    {
      onSuccess: data =>
        void queryClient.setQueryData(
          ["contactsFetch", selectedOrgId],
          () => data
        ),
      onError: () => {
        void queryClient.invalidateQueries(["contactsFetch", selectedOrgId]);
      },
    }
  );
};

export const useCheckContacts = () => {
  const contacts = useFetchContacts();
  if (contacts.isSuccess) {
    if (!contacts.data.updateInfo?.updatedAt) {
      // Is the info verified?
      return false;
    }
    if (
      contacts.data.emailType === ContactType.default &&
      !contacts.data.default.email
    ) {
      // Missing default email, even though it's selected.
      return false;
    }
    if (
      contacts.data.phoneType === ContactType.default &&
      !contacts.data.default.phone
    ) {
      // Missing default phone, even though it's selected.
      return false;
    }
  }
  return true;
};
